import { produce } from 'immer'
import {
  SET_RECEIPT_ATTACHMENT_IDS,
  SetReceiptAttachmentIdsAction,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import { virtualCardsAllCardsRouteProps } from 'src/navigation/routeProps'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/redux/actions/data/receiptAttachments.actions'
import {
  PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE,
  PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT,
  PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS,
  PatchBulkVirtualCardUpdateActions,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT,
  PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS,
  PutBulkVirtualCardDeactivateActions,
} from 'src/redux/actions/data/virtualCards.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import UsersFullPaginatedDataReducer, {
  Actions as UsersFullPaginatedDataActions,
  initialState as initialUsersFullPaginatedDataState,
  UsersFullPaginatedDataState,
} from 'src/redux/usersFullPaginatedData.reducer'
import VirtualCardsFullPaginatedDataReducer, {
  Actions as VirtualCardsFullPaginatedDataActions,
  initialState as initialVirtualCardsFullPaginatedDataState,
  VirtualCardsFullPaginatedDataState,
} from 'src/redux/virtualCardsFullPaginatedData.reducer'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface VirtualCardsAllCardsState {
  receiptAttachmentIds: string[]
  uploadingAttachmentThumbnails: Record<string, string>
  selectedVirtualCardIds?: string[]
  usersFullPaginatedData: UsersFullPaginatedDataState
  virtualCardsFullPaginatedData: VirtualCardsFullPaginatedDataState
  bulkUpdating: boolean
  bulkUpdateTopLevelError?: string
  bulkUpdateErrorDetailMap?: ErrorDetailMap<string>
  bulkDeactivating: boolean
}

export const initialState: VirtualCardsAllCardsState = {
  receiptAttachmentIds: [],
  uploadingAttachmentThumbnails: {},
  usersFullPaginatedData: initialUsersFullPaginatedDataState,
  virtualCardsFullPaginatedData: initialVirtualCardsFullPaginatedDataState,
  bulkUpdating: false,
  bulkDeactivating: false,
}

export type Actions =
  | UsersFullPaginatedDataActions
  | VirtualCardsFullPaginatedDataActions
  | UploadAttachmentActions
  | SetReceiptAttachmentIdsAction
  | PatchBulkVirtualCardUpdateActions
  | PutBulkVirtualCardDeactivateActions

const CoreVirtualCardsAllCardsReducer = (
  state = initialState,
  action: Actions,
): VirtualCardsAllCardsState => ({
  ...produce(state, (draft) => {
    switch (action.type) {
      case SET_RECEIPT_ATTACHMENT_IDS: {
        draft.receiptAttachmentIds = action.payload.ids
        return draft
      }

      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== action.payload.uploadId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.receiptAttachmentIds = [
          ...draft.receiptAttachmentIds,
          action.payload.id,
        ]
        return draft
      }

      case PUT_BULK_VIRTUAL_CARD_DEACTIVATE_INIT: {
        draft.bulkDeactivating = true
        return draft
      }

      case PUT_BULK_VIRTUAL_CARD_DEACTIVATE_SUCCESS:
      case PUT_BULK_VIRTUAL_CARD_DEACTIVATE_FAILURE: {
        draft.bulkDeactivating = false
        return draft
      }

      case PATCH_BULK_VIRTUAL_CARD_UPDATE_INIT: {
        draft.bulkUpdating = true
        draft.bulkUpdateTopLevelError = undefined
        draft.bulkUpdateErrorDetailMap = undefined
        return draft
      }

      case PATCH_BULK_VIRTUAL_CARD_UPDATE_SUCCESS: {
        draft.bulkUpdating = false
        return draft
      }

      case PATCH_BULK_VIRTUAL_CARD_UPDATE_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.bulkUpdating = false
        draft.bulkUpdateTopLevelError = errors.topLevelError
        draft.bulkUpdateErrorDetailMap = errors.errorDetailMap
        return draft
      }
    }
  }),
  usersFullPaginatedData: UsersFullPaginatedDataReducer(
    state.usersFullPaginatedData,
    action as UsersFullPaginatedDataActions,
  ),
  virtualCardsFullPaginatedData: VirtualCardsFullPaginatedDataReducer(
    state.virtualCardsFullPaginatedData,
    action as VirtualCardsFullPaginatedDataActions,
  ),
})

const VirtualCardsAllCardsReducer = createBackgroundReducer(
  virtualCardsAllCardsRouteProps,
  initialState,
  CoreVirtualCardsAllCardsReducer,
)

export default VirtualCardsAllCardsReducer
